/* eslint react/jsx-props-no-spreading: 0 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import PropTypes from "prop-types";
import React from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";

import { isFileFolderName } from "../../../common/FileUtil";
import { curveCsvName, graphType } from "../../../model/Common";
import {
  fetchClearDialog,
  fetchReadCsv,
  fetchUploadReferenceLine,
  selector as referenceLineStoreSelector,
} from "../../../store/ReferenceLineStore";
import { fetchRegisterCurveDataFile } from "../../../store/RegisterStore";
import useStyles from "../useStyles";

import PressManagementActionTable from "./PressManagementActionTable";
import PressManagementDialogFormArea from "./PressManagementDialogFormArea";

// アップロード対象にオブジェクトが含まれているかの判定（true：含まれている、false：含まれていない）
const isIncludesFile = (target, accept, key) => {
  if (target && target[key] === accept[key]) {
    return true;
  }
  return false;
};

// アップロードボタン非活性化判定(true：非活性、false：活性)
const isDisabledUploadButton = (
  serialNumber,
  file,
  legendData,
  referenceLines
) => {
  // グラフ線図データが登録済み
  if (referenceLines.length > 0) {
    if (!file) {
      // 凡例入力フォームエラー(空文字)
      if (!legendData.map((data) => data.error).includes(true)) {
        return false;
      }
    }
  }

  // ファイル選択
  if (file) {
    // ファイル命名規則
    if (file.path === curveCsvName(serialNumber)) {
      // 凡例入力フォームエラー(空文字)
      if (!legendData.map((data) => data.error).includes(true)) {
        return false;
      }
    }
  }

  return true;
};

/**
 * PressManagementDialogコンポーネント.
 *
 * @param {boolean} isOpenDialog ダイアログの表示状態
 * @param {function} onClose ダイアログクローズ時の処理
 * @param {string} serialNumber 工番
 * @param {string} label 拠点名
 * @return {object} コンポーネント
 */
function PressManagementDialog(props) {
  // 引数
  const {
    // ダイアログの表示状態（true：表示、false：非表示）
    isOpenDialog,
    // ダイアログクローズ時の処理
    onClose,
    // 工番
    serialNumber,
    // 拠点名
    label,
    sortState,
  } = props;

  // スタイル
  const styles = useStyles();

  // Dispatch
  const dispatch = useDispatch();

  // アップロード対象ファイル
  const [targetFile, setTargetFile] = React.useState(undefined);

  const {
    // 選択されたファイル一覧
    acceptedFiles,
    // RootProps
    getRootProps,
    // InputProps
    getInputProps,
  } = useDropzone();

  // Store情報
  const {
    // グラフ線図情報(DB取得)
    referenceLines,
    // 凡例名称情報
    legendData,
    // CSVデータ
    csvData,
  } = useSelector(referenceLineStoreSelector);

  // 副作用
  React.useEffect(() => {
    if (acceptedFiles && acceptedFiles.length) {
      const acceptedFile = acceptedFiles[0];
      // ファイル名のバリデーションチェック
      // 選択されたファイルがアップロード対象ファイル一覧に含まれているか判定
      if (
        isFileFolderName(acceptedFile.name) &&
        acceptedFile.name.length <= 50 &&
        !isIncludesFile(targetFile, acceptedFile, "path")
      ) {
        // アップロード対象ファイルを更新
        setTargetFile(acceptedFile);

        // ファイル内容を読み込み
        dispatch(fetchReadCsv(serialNumber, acceptedFile));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, acceptedFiles]);

  // ダイアログクロージ時の処理
  const handleDialogClose = () => {
    dispatch(fetchClearDialog());
    setTargetFile(undefined);
    onClose();
  };

  // アップロードボタン押下時の処理
  const handleUploadButtonClick = () => {
    if (targetFile) {
      dispatch(
        fetchRegisterCurveDataFile(`${serialNumber}/UPLOAD/CurveData/`, [
          targetFile,
        ])
      );
    }
    dispatch(
      fetchUploadReferenceLine(serialNumber, csvData, legendData, sortState)
    );
    setTargetFile(undefined);
    handleDialogClose();
  };

  // 選択済みファイルの解除ボタン押下時の処理
  const handleFileCancelButtonClick = () => {
    // 選択済みファイル情報を削除
    dispatch(fetchReadCsv(undefined));
    setTargetFile(undefined);
  };

  return (
    <Dialog
      open={isOpenDialog}
      fullWidth
      maxWidth="xl"
      onClose={handleDialogClose}
    >
      <DialogTitle>{`${serialNumber} : ${label}`}</DialogTitle>
      <Grid className={styles.reportGrid} container spacing={1}>
        <Grid item xs={4}>
          <DialogContent className={styles.dialogContent}>
            <Paper
              variant="outlined"
              square
              {...getRootProps()}
              className={styles.dragAndDropPaper}
            >
              <input {...getInputProps()} />
              <Typography variant="h6">
                ここにファイルをドラッグ＆ドロップ
              </Typography>
              <Typography variant="h6">もしくは、クリックして選択</Typography>
              <br />
              <br />
              <Typography variant="body2">【注意事項】</Typography>
              <Typography variant="body2">ファイルは1件のみ選択可能</Typography>
              <Typography variant="body2">
                使用可能文字で50文字以内のファイルのみ選択可能
              </Typography>
              <Typography variant="body2">
                工番が一致する「CURVE_*.csv」のみ内容を読み込み
              </Typography>
            </Paper>
          </DialogContent>
          <DialogContent className={styles.dialogContent}>
            <PressManagementActionTable
              fileInfo={targetFile}
              onCancel={handleFileCancelButtonClick}
            />
          </DialogContent>
        </Grid>
        <Grid container item xs={8} spacing={4}>
          <PressManagementDialogFormArea type={graphType.lr} />
          <PressManagementDialogFormArea type={graphType.fb} />
        </Grid>
      </Grid>
      <DialogActions className={styles.dialogActions}>
        <Button color="default" onClick={handleDialogClose}>
          CANCEL
        </Button>
        <Button
          color="primary"
          onClick={handleUploadButtonClick}
          disabled={isDisabledUploadButton(
            serialNumber,
            targetFile,
            legendData,
            referenceLines
          )}
        >
          UPLOAD
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PressManagementDialog.propTypes = {
  isOpenDialog: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  serialNumber: PropTypes.string,
  label: PropTypes.string,
  sortState: PropTypes.shape({
    key: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
};

PressManagementDialog.defaultProps = {
  serialNumber: "",
  label: "",
};

export default PressManagementDialog;
